<script setup lang="ts">
import { PropertyType } from '@/backend/types'
import { FeatureFlag } from '@/modules/App/featureFlags'
import { useFeatureFlags } from '@/modules/App/useFeatureFlags'
import AvatarIcon from '@/uiKit/AvatarIcon.vue'
import DarwinButton from '@/uiKit/DarwinButton.vue'
import DividerLine from '@/uiKit/DividerLine.vue'
import IconSprite from '@/uiKit/IconSprite.vue'
import ToolTip from '@/uiKit/ToolTip.vue'
import { computed } from 'vue'
import type { Field, FieldValueInfo } from './Fields/types'
import FieldTooltipCitationsButton from './FieldTooltipCitationsButton.vue'
import FieldTooltipLockButton from './FieldTooltipLockButton.vue'
import FieldTooltipReasoningButton from './FieldTooltipReasoningButton.vue'
import FieldTooltipRecomputeButton from './FieldTooltipRecomputeButton.vue'
import FieldTooltipSkipButton from './FieldTooltipSkipButton.vue'

const props = defineProps<{
  fieldHasGroundTruth?: boolean
  valueInfo?: FieldValueInfo
  reasoning?: string | null
  hasGroundingClaim?: boolean
  propertyType: PropertyType
  status: Field['status']
}>()

defineEmits<{
  (
    e:
      | 'open'
      | 'reset'
      | 'lock'
      | 'recompute'
      | 'skip'
      | 'open-mousedown'
      | 'reset-mousedown'
      | 'lock-mousedown'
      | 'recompute-mousedown'
      | 'skip-mousedown',
  ): void
}>()

const labelId = 'project-table-cell-tooltip-label'

const isCollectionField = computed(
  () =>
    props.propertyType === PropertyType.collection ||
    props.propertyType === PropertyType.file_collection,
)

const isSkippingFieldsEnabled = useFeatureFlags(FeatureFlag.SKIPPING_FIELDS)
const isSkippableField = computed(() => isSkippingFieldsEnabled.value && props.status === 'idle')
</script>

<template>
  <div
    class="flex items-center rounded-corner-8 bg-surface-popover-inverted"
    data-theme="dark"
  >
    <!-- value info icon/avatar -->
    <div
      v-if="valueInfo"
      class="p-1.5"
    >
      <AvatarIcon
        v-if="
          (valueInfo?.type === 'user' || valueInfo?.type === 'toolWithCorrection') &&
          valueInfo?.userAvatarUrl
        "
        :url="valueInfo?.userAvatarUrl"
        :full-text="valueInfo?.userName"
        shape="circle"
        size="xs"
      />
      <IconSprite
        v-else
        class="text-text-inverted-irreversible"
        :icon="valueInfo?.type === 'user' ? 'user' : valueInfo?.toolIcon"
        size="md"
      />
    </div>

    <!-- value info label -->
    <div
      v-if="valueInfo?.type === 'tool' || valueInfo?.type === 'toolWithCorrection'"
      :id="labelId"
      class="grow cursor-default py-1.5 pr-2 text-sm-12px-light text-text-inverted-irreversible"
    >
      {{
        valueInfo?.type === 'tool'
          ? `Computed by ${valueInfo?.toolLabel}`
          : `Corrected by ${valueInfo?.userName}`
      }}
    </div>

    <!-- no valueInfo (idle/empty state) -->
    <ToolTip
      v-else-if="isSkippableField"
      title="The field is empty."
      :auto-placement-options="{ allowedPlacements: ['top', 'top-end'] }"
      :offset="2"
    >
      <div class="flex flex-row items-center gap-1 px-1.5">
        <IconSprite
          class="text-text-inverted-irreversible"
          icon="empty"
          size="md"
        />
        <div
          :id="labelId"
          class="grow cursor-default py-1.5 pr-2 text-sm-12px-light text-text-inverted-irreversible"
        >
          Idle
        </div>
      </div>
    </ToolTip>

    <DividerLine
      direction="vertical"
      color="default"
    />

    <DarwinButton
      v-if="valueInfo?.type === 'toolWithCorrection'"
      size="sm"
      variant="transparent"
      class="w-full rounded-none"
      @click="$emit('reset')"
      @mousedown="$emit('reset-mousedown')"
    >
      Reset
    </DarwinButton>

    <FieldTooltipSkipButton
      v-if="valueInfo?.type === 'tool' && isSkippableField"
      @skip="$emit('skip')"
      @skip-mousedown="$emit('skip-mousedown')"
    />

    <FieldTooltipRecomputeButton
      v-if="valueInfo?.type === 'tool' && !isSkippableField"
      :status="status"
      @recompute="$emit('recompute')"
      @recompute-mousedown="$emit('recompute-mousedown')"
    />

    <FieldTooltipSkipButton
      v-if="!valueInfo && isSkippableField"
      @skip="$emit('skip')"
      @skip-mousedown="$emit('skip-mousedown')"
    />

    <template v-if="reasoning">
      <DividerLine
        direction="vertical"
        color="default"
      />
      <FieldTooltipReasoningButton :reasoning="reasoning" />
    </template>

    <template v-if="!isCollectionField && valueInfo?.type !== 'toolWithCorrection'">
      <DividerLine
        direction="vertical"
        color="default"
      />
      <FieldTooltipLockButton
        :field-has-ground-truth="fieldHasGroundTruth"
        @lock="$emit('lock')"
        @lock-mousedown="$emit('lock-mousedown')"
      />
    </template>

    <template v-if="hasGroundingClaim">
      <DividerLine
        direction="vertical"
        color="default"
      />
      <FieldTooltipCitationsButton
        @open="$emit('open')"
        @open-mousedown="$emit('open-mousedown')"
      />
    </template>
  </div>
</template>
